import React, { useState, useMemo } from 'react';
import { X, Plus, Loader2, Save } from 'lucide-react';
import { labourService } from '../../../services/labourService';
import SearchSelect from '../../../components/SearchSelect';

// =============================================================================
// Add / edit a labour function — the Function selector's "+ New" and "Edit" on
// Variable Labour. Shared by Distribution and Manufacturing; the caller passes
// the module, and the site comes from the top bar, so a function belongs to one
// team at one site.
//
// A function is the thing being shift-planned: Picking, Packing, Receiving in a
// DC; Extrusion, Blending, Packaging in a plant. Basic, overtime and allowance
// each post to their own cost centre / GL account, because overtime and
// allowances usually land on different lines to basic wages.
//
// NO pay amounts here — this module stays pay-free. The overtime MULTIPLIER is
// a rate factor, not a rand value; the rands come from People.
// =============================================================================

const BLANK = {
  function_code: '', function_name: '',
  cost_center_code: '', gl_account_code: '',
  overtime_multiplier: '1.5', overtime_cost_center_code: '', overtime_gl_account_code: '',
  allowance_cost_center_code: '', allowance_gl_account_code: '',
};

const AddLabourFunctionModal = ({
  moduleCode,
  initial = null,           // pass a function row to EDIT it; null adds a new one
  existingCodes = [],
  costCenters = [],
  glAccounts = [],
  onSaved,
  onClose,
}) => {
  const editing = !!initial;
  const [form, setForm] = useState(() => ({
    ...BLANK,
    ...(initial
      ? {
          function_code: initial.function_code || '',
          function_name: initial.function_name || '',
          cost_center_code: initial.cost_center_code || '',
          gl_account_code: initial.gl_account_code || '',
          overtime_multiplier: String(initial.overtime_multiplier ?? '1.5'),
          overtime_cost_center_code: initial.overtime_cost_center_code || '',
          overtime_gl_account_code: initial.overtime_gl_account_code || '',
          allowance_cost_center_code: initial.allowance_cost_center_code || '',
          allowance_gl_account_code: initial.allowance_gl_account_code || '',
        }
      : {}),
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Both masters are long enough that a scrolling <select> was the wrong
  // control — a chart of accounts especially. SearchSelect matches on code AND
  // name, so "5201" and "wages" both find the wages account.
  const ccOptions = useMemo(
    () => costCenters.map((c) => ({ code: c.code, name: c.name })), [costCenters]);
  const glOptions = useMemo(
    () => glAccounts.map((a) => ({ code: a.code, name: a.name })), [glAccounts]);

  const trimmed = form.function_code.trim().toUpperCase();
  const duplicate = !editing && existingCodes.some((c) => c.toUpperCase() === trimmed);
  const multiplier = Number(form.overtime_multiplier);
  const problem = duplicate ? 'A function with this code already exists at this site.'
    : (!Number.isFinite(multiplier) || multiplier < 0) ? 'Overtime multiplier must be a positive number.'
    : '';
  const canSave = trimmed && form.function_name.trim() && !problem && !busy;

  const submit = async () => {
    if (!canSave) return;
    setBusy(true);
    setError('');
    try {
      const res = await labourService.saveFunction(moduleCode, {
        ...form,
        function_code: trimmed,
        function_name: form.function_name.trim(),
        overtime_multiplier: multiplier,
      });
      if (res?.success) onSaved(trimmed);
      else setError(res?.message || 'Could not save the function.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save the function.');
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-slate-400';
  const label = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
  const legend = 'm-0 mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400';

  // One cost-centre + GL pair. Every posting group on this form is the same
  // shape. Rendered as a plain call rather than <Posting …/>: declaring a
  // component inside the render body gives it a NEW identity every keystroke,
  // which unmounts and remounts the whole group and drops focus mid-search.
  const posting = (title, ccKey, glKey) => (
    <section key={ccKey}>
      <p className={legend}>{title}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={label} htmlFor={ccKey}>Cost centre</label>
          <SearchSelect
            id={ccKey}
            value={form[ccKey]}
            onChange={(code) => set(ccKey, code)}
            options={ccOptions}
            emptyText="No cost centre matches."
          />
        </div>
        <div>
          <label className={label} htmlFor={glKey}>GL account</label>
          <SearchSelect
            id={glKey}
            value={form[glKey]}
            onChange={(code) => set(glKey, code)}
            options={glOptions}
            emptyText="No GL account matches."
          />
        </div>
      </div>
    </section>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-center justify-between">
          <div>
            <h3 className="m-0 text-base font-semibold text-slate-900 dark:text-slate-100">{editing ? 'Edit function' : 'Add function'}</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-400">A team at this site whose shifts are planned together.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label} htmlFor="fn-code">Code</label>
              <input id="fn-code" value={form.function_code} onChange={(e) => set('function_code', e.target.value)}
                placeholder="PICKING" autoFocus={!editing} disabled={editing}
                className={`${field} font-mono uppercase disabled:opacity-60`} />
            </div>
            <div>
              <label className={label} htmlFor="fn-name">Description</label>
              <input id="fn-name" value={form.function_name} onChange={(e) => set('function_name', e.target.value)}
                placeholder="Order picking" autoFocus={editing} className={field} />
            </div>
          </div>

          {posting('Basic wages post to', 'cost_center_code', 'gl_account_code')}

          <section>
            <p className={legend}>Overtime</p>
            <div className="mb-3">
              <label className={label} htmlFor="fn-ot-mult">Multiplier</label>
              <input id="fn-ot-mult" type="number" step="0.1" min="0" value={form.overtime_multiplier}
                onChange={(e) => set('overtime_multiplier', e.target.value)} className={`${field} w-28`} />
              <p className="m-0 mt-1 text-[11px] text-slate-400">e.g. 1.5 for time-and-a-half. Rates themselves come from People.</p>
            </div>
          </section>
          {posting('Overtime posts to', 'overtime_cost_center_code', 'overtime_gl_account_code')}
          {posting('Allowances post to', 'allowance_cost_center_code', 'allowance_gl_account_code')}

          {(problem || error) && <p className="m-0 text-xs text-rose-600 dark:text-rose-400">{problem || error}</p>}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <button type="button" onClick={onClose}
            className="px-3 py-2 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSave}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md text-white bg-slate-900 hover:bg-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editing ? <Save className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />)}
            {editing ? 'Save function' : 'Add function'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLabourFunctionModal;
